import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Icon } from "@vds/icons";
import { calculateRem } from "@uie/javascript-style-helpers";

const defaultProps = {
  disabled: false,
  error: false,
  iconName: null,
  size: 18,
  locked: false
};

const propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  iconName: PropTypes.string,
  size: PropTypes.number,
  locked: PropTypes.bool
};

const StyledInput = styled.input`
  background-color: ${({ theme }) => theme.secondary};
  border-bottom: 0.0625rem solid ${({ theme }) => theme.black};
  border-left: ${calculateRem(1)} solid ${({ theme }) => theme.coolGray3};
  border-radius: 0;
  border-right: ${calculateRem(1)} solid ${({ theme }) => theme.coolGray3};
  border-top: ${calculateRem(1)} solid ${({ theme }) => theme.coolGray3};
  box-sizing: border-box;
  color: ${({ theme }) => theme.black};
  display: inline-block;
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${calculateRem(14)};
  font-weight: normal;
  height: ${calculateRem(42)};
  line-height: 1;
  margin: 0;
  max-width: ${calculateRem(596)};
  padding: ${calculateRem(15)};
  position: relative;
  vertical-align: top;
  width: ${calculateRem(135)};

  ${({ error, theme }) =>
    error &&
    `
    border-color: ${theme.error}
    border-bottom-width: ${calculateRem(4)};
    color: ${theme.black};
    padding-bottom: ${calculateRem(12)};

    &:focus {
      color: ${theme.black};
    }
  `};

  ${({ locked }) =>
    locked &&
    `
    border: none;
    padding-left: 0;
  `};

  ${({ iconName, error, size }) => {
    if (iconName && error) {
      return `padding: ${calculateRem(4, 1, 1, size ? size + 16 : 30)};`;
    }
    if (iconName) {
      return `padding: ${calculateRem(1, 1, 1, size ? size + 16 : 30)};`;
    }
  }}

  ${({ disabled, theme }) =>
    disabled &&
    `
    color: ${theme.disabled}
    border-color: ${theme.disabled}
  `};

  ${({ iconName, error }) => {
    if (!iconName && error) {
      return "padding-top: none";
    }
  }}

  &:focus {
    outline: none;
    border-top: ${calculateRem(1)} solid ${({ theme }) => theme.black};
    border-left: ${calculateRem(1)} solid ${({ theme }) => theme.black};
    border-right: ${calculateRem(1)} solid ${({ theme }) => theme.black};

    ${({ locked }) => locked && "border: none;"};
  }

  ::placeholder {
    color: ${({ disabled, theme }) => (disabled ? "transparent" : theme.coolGray3)};
  }
`;

const IconContainer = styled.span`
  position: absolute;
  z-index: 1;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-left: ${calculateRem(10)};

  ${({ disabled, theme }) =>
    disabled &&
    `
    color: ${theme.disabled}
    border-color: ${theme.disabled}
  `};
`;
const StyledContainer = styled.div`
  display: inline-block;
  position: relative;
  width: 100%;
`;

const renderIcon = (size, { disabled, error, iconName }) => (
  <IconContainer disabled={disabled}>
    <Icon size={size} name={iconName} lineColor={disabled || error ? "#747676" : "#000000"} /> 
  </IconContainer>
);

// destructure size prop so it is not passed via ...props
// onto StyleInput which cause a prop type warning
const Input = ({ size, locked, input, ...props }) => {
  return (
    <StyledContainer>
      {props.iconName && renderIcon(size, props)}
      <StyledInput
        type={props.type ? props.type : "text"}
        size={size}
        locked={locked}
        readOnly={locked}
        {...input}
        {...props}
      />
    </StyledContainer>
  );
};

Input.defaultProps = defaultProps;
Input.propTypes = propTypes;

export default Input;
